import { read, utils } from '@e965/xlsx';

const MAX_IMPORT_FILE_BYTES = 10 * 1024 * 1024;
const MAX_IMPORT_ROWS = 5000;

const valueToString = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value).trim();
  return '';
};

const isBlankRow = (values: string[]): boolean => {
  return values.every((value) => value.trim().length === 0);
};

export interface IeImportPayloadRow {
  rowNumber: number;
  values: Record<string, unknown>;
}

export interface ParsedIeImportFile {
  fileName: string;
  rows: IeImportPayloadRow[];
}

export const parseIeImportFile = async (file: File): Promise<ParsedIeImportFile> => {
  if (file.size <= 0) {
    throw new Error('Import file is empty.');
  }

  if (file.size > MAX_IMPORT_FILE_BYTES) {
    throw new Error('Import file exceeds maximum allowed size of 10 MB.');
  }

  let workbook;
  try {
    const buffer = await file.arrayBuffer();
    workbook = read(buffer, { type: 'array', raw: false });
  } catch {
    throw new Error(`Unable to read "${file.name}". Upload a valid .xlsx, .xls, or .csv file.`);
  }

  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) {
    throw new Error('Import file does not contain a worksheet.');
  }

  const sheet = workbook.Sheets[firstSheetName];
  if (!sheet) {
    throw new Error('Import file worksheet is unavailable.');
  }

  const matrix = utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: '',
    raw: false,
  });

  if (matrix.length < 2) {
    throw new Error('Import file must include a header row and at least one data row.');
  }

  const headerRow = (matrix[0] ?? []).map(valueToString);
  const headers = headerRow.map((header, index) => (header ? header : `Column ${index + 1}`));

  const rows: IeImportPayloadRow[] = [];

  matrix.slice(1).forEach((rowValues, index) => {
    const values = Array.isArray(rowValues) ? rowValues.map(valueToString) : [];
    if (isBlankRow(values)) {
      return;
    }

    const rowRecord: Record<string, unknown> = {};
    headers.forEach((header, columnIndex) => {
      rowRecord[header] = values[columnIndex] ?? '';
    });

    rows.push({
      rowNumber: index + 2,
      values: rowRecord,
    });
  });

  if (rows.length === 0) {
    throw new Error('Import file has no non-empty data rows.');
  }

  if (rows.length > MAX_IMPORT_ROWS) {
    throw new Error(`Import file exceeds maximum supported rows (${MAX_IMPORT_ROWS}).`);
  }

  return {
    fileName: file.name,
    rows,
  };
};
